import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@core/pipes/translate.pipe';
import { TranslationService } from '@core/services/translation.service';
import { AuthService } from '@modules/auth/services/auth.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';

@Component({
  selector: 'app-reset-password',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    RouterLink,
    FormsModule,
    SelectModule,
    AsyncPipe,
    TranslatePipe,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  resetPassForm!: FormGroup;
  builder = inject(FormBuilder);
  route = inject(ActivatedRoute);
  router = inject(Router);
  mode = signal<'reset' | 'set' | string>('');

  constructor(
    public authService: AuthService,
    public translateService: TranslationService,
    private messageService: MessageService,
  ) {
    this.resetPassForm = this.builder.group(
      {
        newPassword: ['', [Validators.required]],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordsMatchValidator },
    );
  }

  ngOnInit(): void {
    const url = this.router.url;
    this.mode.set(url.includes('reset-password') ? 'reset' : 'set');
  }

  switchLang(lang: string) {
    this.translateService.setLanguage(lang);
  }

  // Custom validator
  passwordsMatchValidator(control: AbstractControl) {
    const password = control.get('newPassword')?.value;
    const confirm = control.get('confirmPassword')?.value;
    return password === confirm ? null : { passwordMismatch: true };
  }

  resetForgotPassword() {
    if (!this.resetPassForm.valid) {
      if (this.resetPassForm.hasError('passwordMismatch')) {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Password did not match',
        });
      }
      this.resetPassForm.markAllAsTouched();
      return;
    }

    const token = this.route.snapshot.params['token'] || '';
    const payload = { password: this.resetPassForm.get('newPassword')?.value };
    const setPasswordApi =
      this.mode() === 'reset'
        ? this.authService.resetPassword(payload, token)
        : this.authService.setPassword(payload, token);

    setPasswordApi.subscribe({
      next: (res) => {
        if (res && res['success']) {
          this.router.navigate(['/auth', 'login']);
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: res.message,
          });
        }
      },
    });
  }
}
